import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import useAssetMedia from '../../hooks/useAssetMedia';
import useTags from '../../hooks/useTags';
import { can } from '../../auth/permissions';
import { useCurrentUser } from '../../auth/CurrentUserContext';
import formatBytes from '../../../shared/utils/formatBytes';
import TagRelationPicker from '../tags/TagRelationPicker';
import StatusPill from '../../../shared/components/pill/StatusPill';
import { uploadStatus, knowledgeIndexFileStatus } from '../../utils/mediaStatus';

export default function AssetMediaPanel({ assetId }) {
  const { user } = useCurrentUser();
  const canWrite = can(user, 'assets.write');
  const fileInputRef = useRef(null);
  const {
    items,
    loading,
    error,
    uploading,
    uploadFile,
    deleteMedia,
    downloadMedia,
    setFieldEngineerVisible,
    setTags,
    retryKnowledgeIndex,
  } = useAssetMedia(assetId);
  const { tags } = useTags();

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await uploadFile(file);
    } catch {
      // error surfaced via hook state
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteMedia(mediaId);
    } catch {
      // error surfaced via hook state
    }
  };

  return (
    <div className="card p-3 mt-4 min-w-0 overflow-hidden">
      <div className="d-flex align-items-center mb-3">
        <h5 className="fw-semibold mb-0">Files &amp; media</h5>
        {canWrite && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary ms-auto"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Uploading…
              </>
            ) : (
              <>
                <i className="bi bi-upload me-1" />
                Upload
              </>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          onChange={handleFileChange}
          accept=".pdf,.html,.htm,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.webp,image/*,application/pdf,text/html"
        />
      </div>

      <p className="text-muted small mb-3">
        Manuals, installation photos, and other documents for this asset.
        Toggle &ldquo;Share with field engineers&rdquo; to show files on the asset page and enable
        the AI assistant for PDF, HTML, TXT, CSV, and Excel files (field engineers with a profile only).
      </p>

      {error && (
        <div className="alert alert-danger py-2 small mb-2">{error}</div>
      )}

      {loading && (
        <div className="text-muted small">
          <span className="spinner-border spinner-border-sm me-2" role="status" />
          Loading files…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-muted small">No files uploaded yet.</div>
      )}

      {!loading && items.map((item) => {
        const badge = uploadStatus(item.status);
        const aiBadge = knowledgeIndexFileStatus(item);
        return (
          <div
            key={item.id}
            className="border rounded bg-light p-2 px-3 file-list-row file-list-row--top mb-2"
          >
            <i className="bi bi-file-earmark text-secondary mt-1" aria-hidden />
            <div className="min-w-0">
              <div className="fw-semibold file-list-row__name" title={item.filename}>
                {item.filename}
              </div>
              <div className="small text-muted">
                {formatBytes(item.byte_size)}
                {badge && (
                  <StatusPill tone={badge.tone} size="inline" className="ms-2">
                    {badge.label}
                  </StatusPill>
                )}
                {aiBadge && (
                  <StatusPill
                    tone={aiBadge.tone}
                    size="inline"
                    className="ms-2"
                    title={item.knowledge_index_error || undefined}
                  >
                    {aiBadge.label}
                  </StatusPill>
                )}
              </div>
              {canWrite && item.status === 'ready' && (
                <div className="form-check form-switch mt-2 mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`fe-visible-${item.id}`}
                    checked={!!item.visible_to_field_engineers}
                    onChange={(e) => setFieldEngineerVisible(item.id, e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor={`fe-visible-${item.id}`}>
                    Share with field engineers
                  </label>
                </div>
              )}
              {canWrite && item.status === 'ready' && (
                <div className="mt-2" style={{ maxWidth: 420 }}>
                  <TagRelationPicker
                    tags={tags}
                    selectedIds={item.tag_ids || []}
                    pinGlobal
                    onChange={(tagIds) => setTags(item.id, tagIds)}
                  />
                </div>
              )}
            </div>
            <div className="d-flex gap-1">
              {canWrite && item.status === 'ready' && item.visible_to_field_engineers && item.knowledge_index_status === 'failed' && (
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  title="Retry AI indexing"
                  onClick={() => retryKnowledgeIndex(item.id)}
                >
                  <i className="bi bi-arrow-clockwise" />
                </button>
              )}
              {item.status === 'ready' && (
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  title="Download"
                  onClick={() => downloadMedia(item.id)}
                >
                  <i className="bi bi-download" />
                </button>
              )}
              {canWrite && (
                <button
                  type="button"
                  className="btn btn-sm btn-light border text-danger"
                  title="Delete"
                  onClick={() => handleDelete(item.id)}
                >
                  <i className="bi bi-trash" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

AssetMediaPanel.propTypes = {
  assetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
